import { useEffect } from 'react';

import Cal, { getCalApi } from '@calcom/embed-react';
import type { OnboardingOverlayOpenedEndpoint } from '@core/types/types.endpoint.onboarding';
import { Avatar, AvatarImage } from '@frontend/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@frontend/components/ui/dialog';
import {
  useDirectoryMe,
  useDirectoryTeam,
} from '@frontend/shared/hooks/useDirectory';
import { callApi } from '@frontend/shared/util/util.callApi';

export function AppOnboardingBlockingOverlay() {
  const team = useDirectoryTeam();
  const me = useDirectoryMe();

  // Show overlay if:
  // 1. Onboarding is complete (survey submitted)
  // 2. Admin has NOT dismissed overlay via backend
  // 3. Team was created after December 1, 2025 (new customers only)
  const CUTOFF_DATE = new Date('2025-12-01T00:00:00Z');
  const teamCreatedAt = team.created_at ? new Date(team.created_at) : null;
  const isNewCustomer = teamCreatedAt && teamCreatedAt >= CUTOFF_DATE;

  const shouldShow =
    team.onboarding_complete &&
    !team.onboarding_overlay_dismissed &&
    isNewCustomer;

  useEffect(() => {
    if (shouldShow) {
      (async function () {
        // Notify analytics channel that overlay was opened
        callApi<OnboardingOverlayOpenedEndpoint>(
          '/onboarding/overlay/opened',
          {},
        ).catch((error) => {
          // Log error but don't block overlay from showing
          console.error('Failed to track overlay opened:', error);
        });

        const cal = await getCalApi({ namespace: 'onboarding-overlay' });
        cal('ui', {
          hideEventTypeDetails: false,
          layout: 'month_view',
          theme: 'light',
        });
      })();
    }
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  // Permanent modal - cannot be closed by user
  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="max-w-5xl sm:max-w-5xl" showCloseButton={false}>
        <div className="flex flex-col items-center mb-6">
          {/* Avatar row */}
          <div className="flex -space-x-2 mb-4">
            <Avatar className="h-10 w-10 border-2 border-white">
              <AvatarImage src="/assets/portrait-kt.png" alt="Team member" />
            </Avatar>
            <Avatar className="h-10 w-10 border-2 border-white">
              <AvatarImage src="/assets/portrait-yt.png" alt="Team member" />
            </Avatar>
            <Avatar className="h-10 w-10 border-2 border-white">
              <AvatarImage src="/assets/portrait-sv.png" alt="Team member" />
            </Avatar>
          </div>

          <DialogTitle className="text-2xl font-bold text-center">
            Book your free implementation call
          </DialogTitle>
          <p className="text-sm text-gray-600 text-center mt-2">
            Let us help you with the setup → Book a free 15min call
          </p>
        </div>

        {/* Cal.com embed - full width */}
        <div className="-mx-6 -mb-6">
          <Cal
            namespace="onboarding-overlay"
            calLink="team/questionbase/implementation-call"
            style={{ width: '100%', height: '650px' }}
            config={{
              layout: 'month_view',
              theme: 'light',
              name: me.name || '',
              email: me.email || '',
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
